import { useSelector } from 'react-redux';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { colorMap } from '../../../config/layerDictionary.js';

import './style/Graphic.css';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function Graphic() {
	const dashboardState = useSelector(state => state.dashboard);
	const { data } = dashboardState;

	if (!data || !data.porcentagem) {
		return (
			<p className="graph-panel">Nenhum dado disponível no momento.</p>
		);
	}

	const percentage = data.porcentagem;
	const labels = Object.keys(percentage);
	const percentageNumber = Object.values(percentage).map(values => {
		const number = parseFloat(values.replace('%', '').replace(',', '.'));
		return isNaN(number) ? 0 : number;
	});

	const total = percentageNumber.reduce((acc, val) => acc + val, 0);
	if (total < 100) {
		labels.push('À executar');
		percentageNumber.push(100 - total);
	}

	const backgroundColors = labels.map(label => {
		if (label === 'À executar') {
			return '#D9D9D9';
		}
		const statusKey = label || 'Indefinido';
		return colorMap[statusKey] || '#D9D9D9';
	});

	const dataInfo = {
		labels: labels,
		datasets: [
			{
				data: percentageNumber,
				backgroundColor: backgroundColors,
				borderWidth: 1,
			},
		],
	};

	const options = {
		cutout: '70%',
		plugins: {
			legend: { display: false },
			tooltip: {
				enabled: true,
				callbacks: {
					label: function (context) {
						const value = context.parsed;
						const dataArr = context.chart.data.datasets[0].data;
						const total = dataArr.reduce(
							(acc, val) => acc + val,
							0
						);
						const percent = ((value / total) * 100)
							.toFixed(2)
							.replace('.', ',');
						return `${context.label}: ${percent}%`;
					},
				},
			},
			datalabels: { display: false },
			centerText: {
				display: true,
				statuses: [
					'Executado',
					'Aprovado com Pendência',
					'Aprovado',
					'Entregue',
				],
			},
		},
	};

	const centerTextOverview = {
		id: 'centerTextSafe',
		afterDatasetsDraw(chart) {
			const pluginOpts =
				chart?.config?.options?.plugins?.centerText ||
				chart?.options?.plugins?.centerText ||
				null;

			if (!pluginOpts || pluginOpts.display === false) {
				return;
			}

			const { ctx, chartArea, data } = chart;
			if (!chartArea || !data) return;

			const labels = Array.isArray(data.labels) ? data.labels : [];
			const dataset = Array.isArray(data.datasets?.[0]?.data)
				? data.datasets[0].data
				: [];

			const alvo = new Set((pluginOpts.statuses || []).map(String));
			const soma = labels.reduce((acc, lbl, i) => {
				const v = Number(dataset[i] ?? 0);
				return acc + (alvo.has(String(lbl)) ? v : 0);
			}, 0);

			const total = dataset.reduce((a, b) => a + Number(b ?? 0), 0);
			const percent =
				total > 0
					? ((soma / total) * 100).toFixed(2).replace('.', ',')
					: '0,00';

			const x = (chartArea.left + chartArea.right) / 2;
			const y = (chartArea.top + chartArea.bottom) / 2;

			const meta = chart.getDatasetMeta(0);
			const arc0 = meta?.data?.[0];
			const innerRadius = Math.max(
				10,
				Number(arc0?.innerRadius ?? 0) ||
					Math.min(
						chartArea.right - chartArea.left,
						chartArea.bottom - chartArea.top
					) * 0.35
			);
			ctx.save();
			ctx.beginPath();
			ctx.arc(x, y, innerRadius - 2, 0, Math.PI * 2);
			const bg = getComputedStyle(chart.canvas).backgroundColor || '#fff';
			ctx.fillStyle = bg;
			ctx.fill();
			ctx.restore();

			ctx.save();
			const diameter = Math.min(
				chartArea.right - chartArea.left,
				chartArea.bottom - chartArea.top
			);
			const fontSize = Math.max(12, Math.floor(diameter * 0.12));
			ctx.font = `bold ${fontSize}px Arial`;
			ctx.fillStyle = '#000';
			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';
			ctx.fillText(`${percent}%`, x, y);
			ctx.restore();
		},
	};

	return (
		<div className="graph-panel">
			<span className="percent-label">% de Km² Executada</span>

			<div className="chart-container">
				<Doughnut
					data={dataInfo}
					options={options}
					plugins={[centerTextOverview]}
				/>
			</div>

			<div className="legend-horizontal">
				{labels.map((label, index) => {
					const value = percentageNumber[index];
					if (value === 0) return null;
					return (
						<div key={label} className="legend-item">
							<div
								className="color-box"
								style={{
									backgroundColor:
										dataInfo.datasets[0].backgroundColor[
											index
										],
								}}
							></div>
							<span>{label}</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}
